import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class FullscreenGalleryScroll extends Component {
  requiredRefs = ['track', 'sticky', 'viewport', 'rail'];

  connectedCallback() {
    super.connectedCallback();

    this.#items = Array.from(this.querySelectorAll('[data-gallery-item]'));
    this.#setupHoverEffects();

    this.#intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
      rootMargin: '200px 0px',
    });
    this.#resizeObserver = new ResizeObserver(() => {
      this.#measure();
      this.#updateTargetProgress();
      this.#requestTick();
    });

    this.#resizeObserver.observe(this.refs.sticky);
    this.#resizeObserver.observe(this.refs.viewport);
    this.#resizeObserver.observe(this.refs.rail);

    this.#intersectionObserver.observe(this.refs.track);

    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize);

    this.#measure();
    this.#updateTargetProgress();
    this.#currentProgress = this.#targetProgress;
    this.#applyTransform(this.#currentProgress);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();

    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);

    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
    }

    for (const item of this.#items) {
      this.#stopHoverSequence(item);
      item.removeEventListener('pointerenter', this.#handleItemPointerEnter);
      item.removeEventListener('pointerleave', this.#handleItemPointerLeave);
      item.removeEventListener('focusin', this.#handleItemFocusIn);
      item.removeEventListener('focusout', this.#handleItemFocusOut);
    }
  }

  /**
   * @type {HTMLElement[]}
   */
  #items = [];

  /**
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  /**
   * @type {number}
   */
  #scrollDistance = 0;

  /**
   * @type {number}
   */
  #stickyHeight = 0;

  /**
   * @type {number}
   */
  #targetProgress = 0;

  /**
   * @type {number}
   */
  #currentProgress = 0;

  /**
   * @type {number}
   */
  #rafId = 0;

  /**
   * @type {boolean}
   */
  #isNearViewport = false;

  /**
   * @type {WeakMap<HTMLElement, number>}
   */
  #hoverTimeouts = new WeakMap();

  /**
   * @type {WeakMap<HTMLElement, number>}
   */
  #hoverIndices = new WeakMap();

  #handleIntersection = ([entry]) => {
    this.#isNearViewport = entry?.isIntersecting ?? false;

    if (this.#isNearViewport) {
      this.#updateTargetProgress();
      this.#requestTick();
    }
  };

  #handleScroll = () => {
    if (this.#isMobile) return;

    this.#updateTargetProgress();

    if (this.#isNearViewport) {
      this.#requestTick();
    }
  };

  #handleResize = () => {
    this.#measure();
    this.#updateTargetProgress();
    this.#requestTick();
  };

  #requestTick() {
    if (this.#isMobile || this.#rafId) return;

    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #tick = () => {
    this.#rafId = 0;

    const nextProgress = prefersReducedMotion()
      ? this.#targetProgress
      : this.#currentProgress + (this.#targetProgress - this.#currentProgress) * this.#scrubFactor;
    const isSettled = Math.abs(nextProgress - this.#targetProgress) < 0.001;

    this.#currentProgress = isSettled ? this.#targetProgress : nextProgress;
    this.#applyTransform(this.#currentProgress);

    if (!isSettled) {
      this.#requestTick();
    }
  };

  get #scrubFactor() {
    const rawValue = Number(this.dataset.scrub ?? 16) / 100;

    return Math.min(Math.max(rawValue, 0.01), 1);
  }

  #measure() {
    if (this.#isMobile) {
      if (this.#rafId) cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
      this.#scrollDistance = 0;
      this.#currentProgress = 0;
      this.style.removeProperty('--fullscreen-gallery-track-height');
      this.style.removeProperty('--fullscreen-gallery-scroll-distance');
      this.style.removeProperty('--fullscreen-gallery-translate');
      return;
    }

    this.refs.viewport.scrollLeft = 0;
    const viewportWidth = this.refs.viewport.clientWidth;
    const railWidth = this.refs.rail.scrollWidth;
    const topOffset = this.#topOffset;

    this.#stickyHeight = this.refs.sticky.offsetHeight;
    this.#scrollDistance = Math.max(railWidth - viewportWidth, 0);

    this.style.setProperty('--fullscreen-gallery-scroll-distance', `${this.#scrollDistance}px`);
    this.style.setProperty(
      '--fullscreen-gallery-track-height',
      `${Math.max(this.#stickyHeight + this.#scrollDistance + topOffset, this.#stickyHeight)}px`
    );
  }

  #updateTargetProgress() {
    if (this.#scrollDistance <= 0) {
      this.#targetProgress = 0;
      return;
    }

    const rect = this.refs.track.getBoundingClientRect();
    const totalScrollableDistance = Math.max(this.#scrollDistance, 1);
    const rawProgress = (this.#topOffset - rect.top) / totalScrollableDistance;

    this.#targetProgress = Math.min(Math.max(rawProgress, 0), 1);
  }

  get #topOffset() {
    return Number.parseFloat(getComputedStyle(this).getPropertyValue('--fullscreen-gallery-height-offset')) || 0;
  }

  get #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * @param {number} progress
   */
  #applyTransform(progress) {
    this.style.setProperty('--fullscreen-gallery-translate', `${this.#scrollDistance * progress}px`);
  }

  #setupHoverEffects() {
    for (const item of this.#items) {
      item.addEventListener('pointerenter', this.#handleItemPointerEnter);
      item.addEventListener('pointerleave', this.#handleItemPointerLeave);
      item.addEventListener('focusin', this.#handleItemFocusIn);
      item.addEventListener('focusout', this.#handleItemFocusOut);
    }
  }

  /**
   * @param {Event} event
   */
  #handleItemPointerEnter = (event) => {
    const item = /** @type {HTMLElement | null} */ (event.currentTarget);

    if (!item) return;

    this.#startHoverSequence(item);
  };

  /**
   * @param {Event} event
   */
  #handleItemPointerLeave = (event) => {
    const item = /** @type {HTMLElement | null} */ (event.currentTarget);

    if (!item) return;

    this.#stopHoverSequence(item);
  };

  /**
   * @param {FocusEvent} event
   */
  #handleItemFocusIn = (event) => {
    const item = /** @type {HTMLElement | null} */ (event.currentTarget);

    if (!item) return;

    this.#startHoverSequence(item);
  };

  /**
   * @param {FocusEvent} event
   */
  #handleItemFocusOut = (event) => {
    const item = /** @type {HTMLElement | null} */ (event.currentTarget);

    if (!item) return;
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;

    this.#stopHoverSequence(item);
  };

  /**
   * @param {HTMLElement} item
   */
  #startHoverSequence(item) {
    const imageCount = Number(item.dataset.imageCount ?? 1);

    this.#stopHoverSequence(item);

    if (imageCount <= 1) return;

    if (prefersReducedMotion()) {
      item.dataset.activeImage = '1';
      this.#hoverIndices.set(item, 1);
      return;
    }

    const delay = Number(item.dataset.hoverDelay ?? 1200);

    const advance = () => {
      const currentIndex = this.#hoverIndices.get(item) ?? 0;
      const nextIndex = (currentIndex + 1) % imageCount;

      this.#hoverIndices.set(item, nextIndex);
      item.dataset.activeImage = String(nextIndex);

      const timeoutId = window.setTimeout(advance, delay);
      this.#hoverTimeouts.set(item, timeoutId);
    };

    const timeoutId = window.setTimeout(advance, delay);
    this.#hoverTimeouts.set(item, timeoutId);
  }

  /**
   * @param {HTMLElement} item
   */
  #stopHoverSequence(item) {
    const timeoutId = this.#hoverTimeouts.get(item);

    if (timeoutId) {
      clearTimeout(timeoutId);
      this.#hoverTimeouts.delete(item);
    }

    this.#hoverIndices.set(item, 0);
    item.dataset.activeImage = '0';
  }
}

if (!customElements.get('fullscreen-gallery-scroll')) {
  customElements.define('fullscreen-gallery-scroll', FullscreenGalleryScroll);
}
